import React, { useState, useEffect, useRef } from "react";
import {
  Box, Card, CardContent, Container, Typography, Grid, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar, Stack, Divider,
  IconButton, Chip, Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db, cloudStorage, uploadImage } from "../database/db";
import Page from "../components/Page";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EMPTY_GIFT = { name: "", tokenValue: "", description: "", imageUrl: "" };

function GiftCard({ gift, onEdit, onDelete }) {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", height: "100%", position: "relative" }}>
      <Box sx={{ position: "absolute", top: 8, right: 8 }}>
        <IconButton size="small" onClick={() => onEdit(gift)}>
          <Icon icon="bx:bxs-edit" width={18} />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => onDelete(gift)}>
          <Icon icon="fe:trash" width={18} />
        </IconButton>
      </Box>
      <CardContent sx={{ textAlign: "center", pt: 3 }}>
        <Avatar
          src={gift.imageUrl}
          sx={{ width: 80, height: 80, mx: "auto", mb: 1.5, borderRadius: 3, bgcolor: "background.default" }}
        >
          <Icon icon="eva:gift-fill" width={40} color="#FF6C40" />
        </Avatar>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{gift.name}</Typography>
        {gift.description && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {gift.description}
          </Typography>
        )}
        <Chip
          icon={<Icon icon="fa-solid:coins" width={14} />}
          label={`${gift.tokenValue} tokens`}
          color="warning"
          size="small"
          sx={{ fontWeight: 700 }}
        />
      </CardContent>
    </Card>
  );
}

export default function TokenGifts() {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "add", gift: EMPTY_GIFT });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef();

  const [tokenSettings, setTokenSettings] = useState({
    tokenPriceUSD: "",
    hostPayoutRate: "",
    minTokenPurchase: "",
  });
  const [tokenSettingsSaved, setTokenSettingsSaved] = useState(false);

  useEffect(() => { loadGifts(); loadTokenSettings(); }, []);

  const loadGifts = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "Gifts"));
      setGifts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error("Failed to load gifts");
    }
    setLoading(false);
  };

  const loadTokenSettings = async () => {
    try {
      const snap = await getDocs(collection(db, "AppSettings"));
      const settings = snap.docs.find((d) => d.id === "tokenSettings");
      if (settings) setTokenSettings(settings.data());
    } catch (_) {}
  };

  const openAdd = () => {
    setDialog({ open: true, mode: "add", gift: { ...EMPTY_GIFT } });
    setImageFile(null);
    setImagePreview("");
  };

  const openEdit = (gift) => {
    setDialog({ open: true, mode: "edit", gift: { ...gift } });
    setImageFile(null);
    setImagePreview(gift.imageUrl || "");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    const { mode, gift } = dialog;
    if (!gift.name || !gift.tokenValue) {
      toast.error("Name and token value are required");
      return;
    }
    setSaving(true);
    try {
      let imageUrl = gift.imageUrl || "";
      if (imageFile) {
        imageUrl = await uploadImage(imageFile, `gifts/${Date.now()}_${imageFile.name}`);
      }
      const payload = {
        name: gift.name,
        tokenValue: parseInt(gift.tokenValue, 10),
        description: gift.description || "",
        imageUrl,
        updatedAt: serverTimestamp(),
      };
      if (mode === "add") {
        await addDoc(collection(db, "Gifts"), { ...payload, createdAt: serverTimestamp() });
        toast.success("Gift added!");
      } else {
        await updateDoc(doc(db, "Gifts", gift.id), payload);
        toast.success("Gift updated!");
      }
      setDialog({ open: false, mode: "add", gift: EMPTY_GIFT });
      loadGifts();
    } catch (err) {
      toast.error("Save failed: " + err.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, "Gifts", deleteTarget.id));
      toast.success("Gift deleted");
      setDeleteTarget(null);
      loadGifts();
    } catch (err) {
      toast.error("Delete failed: " + err.message);
    }
  };

  const handleSaveTokenSettings = async () => {
    try {
      await updateDoc(doc(db, "AppSettings", "tokenSettings"), {
        ...tokenSettings,
        updatedAt: serverTimestamp(),
      }).catch(async () => {
        // doc doesn't exist yet
        const { setDoc, doc: fDoc } = await import("firebase/firestore");
        const { db: fDb } = await import("../database/db");
        await setDoc(fDoc(fDb, "AppSettings", "tokenSettings"), {
          ...tokenSettings,
          updatedAt: serverTimestamp(),
        });
      });
      toast.success("Token settings saved!");
      setTokenSettingsSaved(true);
      setTimeout(() => setTokenSettingsSaved(false), 3000);
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  return (
    <Page title="Token & Gifts | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Token & Gift System</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage in-app gift stickers for livestream tips
          </Typography>
        </Box>

        {/* Token Settings */}
        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Token Economics</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Token Price (USD)" type="number"
                  value={tokenSettings.tokenPriceUSD}
                  onChange={(e) => setTokenSettings((p) => ({ ...p, tokenPriceUSD: e.target.value }))}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                  helperText="How much 1 token costs in USD"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Host Payout Rate (%)" type="number"
                  value={tokenSettings.hostPayoutRate}
                  onChange={(e) => setTokenSettings((p) => ({ ...p, hostPayoutRate: e.target.value }))}
                  helperText="% of token value paid to host"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Min Token Purchase" type="number"
                  value={tokenSettings.minTokenPurchase}
                  onChange={(e) => setTokenSettings((p) => ({ ...p, minTokenPurchase: e.target.value }))}
                  helperText="Minimum tokens per purchase"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained" onClick={handleSaveTokenSettings}
                  sx={{ bgcolor: "#FF6C40" }}
                  startIcon={<Icon icon="eva:save-fill" />}
                >
                  Save Token Settings
                </Button>
                {tokenSettingsSaved && (
                  <Chip label="Saved!" color="success" size="small" sx={{ ml: 2 }} />
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Gift Stickers */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Gift Stickers</Typography>
          <Button variant="contained" startIcon={<Icon icon="eva:plus-fill" />}
            onClick={openAdd} sx={{ bgcolor: "#FF6C40" }}>
            Add Gift
          </Button>
        </Box>

        {loading ? (
          <Typography color="text.secondary">Loading gifts...</Typography>
        ) : gifts.length === 0 ? (
          <Card sx={{ borderRadius: 3, p: 6, textAlign: "center" }}>
            <Icon icon="eva:gift-fill" width={60} color="#FF6C40" />
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>No Gifts Yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add gift stickers that users can send during livestreams
            </Typography>
            <Button variant="contained" sx={{ bgcolor: "#FF6C40" }} onClick={openAdd}>
              Add First Gift
            </Button>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {gifts.map((gift) => (
              <Grid key={gift.id} item xs={6} sm={4} md={3} lg={2}>
                <GiftCard gift={gift} onEdit={openEdit} onDelete={setDeleteTarget} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, mode: "add", gift: EMPTY_GIFT })}
        maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.mode === "add" ? "Add Gift Sticker" : "Edit Gift Sticker"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Image Upload */}
            <Box sx={{ textAlign: "center" }}>
              <Avatar
                src={imagePreview || dialog.gift.imageUrl}
                sx={{ width: 120, height: 120, mx: "auto", mb: 1.5, borderRadius: 3, cursor: "pointer", border: "2px dashed #ccc" }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon icon="eva:image-fill" width={40} color="#ccc" />
              </Avatar>
              <input
                type="file" accept="image/*" style={{ display: "none" }}
                ref={fileInputRef} onChange={handleImageChange}
              />
              <Button size="small" onClick={() => fileInputRef.current?.click()}
                startIcon={<Icon icon="eva:upload-fill" />}>
                Upload Sticker Image
              </Button>
            </Box>
            <TextField
              fullWidth label="Gift Name" required
              value={dialog.gift.name}
              onChange={(e) => setDialog((p) => ({ ...p, gift: { ...p.gift, name: e.target.value } }))}
              placeholder="e.g. Fire Rocket, Diamond, Crown"
            />
            <TextField
              fullWidth label="Token Value" type="number" required
              value={dialog.gift.tokenValue}
              onChange={(e) => setDialog((p) => ({ ...p, gift: { ...p.gift, tokenValue: e.target.value } }))}
              helperText="How many tokens this gift costs"
              InputProps={{ endAdornment: <Typography>tokens</Typography> }}
            />
            <TextField
              fullWidth label="Description" multiline rows={2}
              value={dialog.gift.description}
              onChange={(e) => setDialog((p) => ({ ...p, gift: { ...p.gift, description: e.target.value } }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, mode: "add", gift: EMPTY_GIFT })}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: "#FF6C40" }}>
            {saving ? "Saving..." : dialog.mode === "add" ? "Add Gift" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Gift</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
